import React, { useState, useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useToast } from '@/components/ui/toast/Toast';
import { rentingsApi } from '../api/rentingsApi';
import Button from '@/components/ui/button/Button';
import Label from '@/components/form/Label';
import TextArea from '@/components/form/input/TextArea';
import { withFormProtection } from '@/components/form/withFormProtection';

interface RentingCommentFormProps {
  rentingId: string;
  onSubmit: () => void;
  onClose: () => void;
  initialData?: FormData;
  currentData?: FormData;
  onDataChange?: (data: FormData) => void;
}

interface FormData {
  text: string;
  file: File | null;
}

const RentingCommentForm = React.forwardRef<{ handleClose: () => void; isDirty: () => boolean }, RentingCommentFormProps>(({
  rentingId,
  onSubmit,
  onClose,
  initialData,
  onDataChange,
}, ref) => {
  const { t } = useTranslation();
  const toast = useToast();
  const [text, setText] = useState(initialData?.text || '');
  const [file, setFile] = useState<File | null>(initialData?.file || null);
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  // Notify parent about form data changes
  const handleFormDataChange = useCallback((newText: string, newFile: File | null) => {
    onDataChange?.({
      text: newText,
      file: newFile,
    });
  }, [onDataChange]);

  // Update form data when text changes
  const handleTextChange = (value: string) => {
    setText(value);
    handleFormDataChange(value, file);
    if (!value.trim()) {
      setError(t('rentings.comment.errors.text_required'));
    } else if (error) {
      setError(null);
    }
  };

  // Update form data when file changes
  const handleFileChange = (newFile: File | null) => {
    setFile(newFile);
    handleFormDataChange(text, newFile);
  };

  const createComment = useMutation({
    mutationFn: (data: { text: string; file?: File }) =>
      rentingsApi.createComment(rentingId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rentings', rentingId] });
      onSubmit();
      onClose();
      toast.success(t('rentings.comment.create_success'));
    },
    onError: (error) => {
      toast.error(t('rentings.comment.create_error' + `<br/><br/>${error}`));
    },
  });

  const handleSubmit = async () => {
    if (!text.trim()) {
      setError(t('rentings.comment.errors.text_required'));
      return;
    }

    try {
      await createComment.mutateAsync({
        text: text.trim(),
        file: file || undefined,
      });
    } catch (error) {
      // Error is handled in mutation options
    }
  };

  return (
    <>
      <div className="px-6 py-4">
        <h5 className="text-xl font-semibold text-gray-800 dark:text-white/90 lg:text-2xl">
          {t('rentings.comment.title')}
        </h5>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {t('rentings.comment.description')}
        </p>
      </div>

      <div className="p-6">
        <div className="space-y-4">
          <div>
            <Label>
              {t('rentings.comment.fields.comment')}
              <span className="text-danger ml-1">*</span>
            </Label>
            <TextArea
              value={text}
              onChange={handleTextChange}
              placeholder={t('rentings.comment.placeholders.enter_comment')}
              rows={4}
              error={!!error}
              aria-required="true"
            />
          </div>

          <div>
            <Label>{t('rentings.comment.fields.image')}</Label>
            <div className="mt-2">
              {file ? (
                <div className="relative group w-fit">
                  <div className="w-20 h-20 rounded overflow-hidden bg-gray-100 flex items-center justify-center">
                    <img
                      src={URL.createObjectURL(file)}
                      alt={file.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <button
                    onClick={() => handleFileChange(null)}
                    className="absolute -top-2 -right-2 bg-white rounded-full p-1 shadow-md hover:bg-gray-100"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4 text-gray-500"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                </div>
              ) : (
                <label className="w-20 h-20 flex items-center justify-center border-2 border-dashed border-gray-300 rounded cursor-pointer hover:border-primary">
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => {
                      const selectedFile = e.target.files?.[0];
                      if (selectedFile) {
                        handleFileChange(selectedFile);
                      }
                    }}
                  />
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6 text-gray-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 4v16m8-8H4"
                    />
                  </svg>
                </label>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="px-6 py-4">
        <div className="flex items-center gap-3 sm:justify-end">
          <Button variant="outline" size="sm" onClick={onClose}>
            {t('common.buttons.cancel')}
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleSubmit}
            disabled={createComment.isPending}
          >
            {createComment.isPending ? t('rentings.comment.buttons.adding') : t('rentings.comment.buttons.add')}
          </Button>
        </div>
      </div>
    </>
  );
});

RentingCommentForm.displayName = 'RentingCommentForm';

const ProtectedRentingCommentForm = withFormProtection(RentingCommentForm);

interface RentingCommentFormWrapperProps extends Omit<RentingCommentFormProps, 'currentData' | 'onDataChange' | 'initialData'> {
  ref?: React.Ref<{ handleClose: () => void; isDirty: () => boolean }>;
}

const RentingCommentFormWrapper = React.forwardRef<{ handleClose: () => void; isDirty: () => boolean }, RentingCommentFormWrapperProps>((props, ref) => {
  const initialData = {
    text: '',
    file: null,
  };

  const [currentData, setCurrentData] = useState<FormData>(initialData);

  const handleDataChange = useCallback((data: FormData) => {
    setCurrentData(data);
  }, []);

  return (
    <ProtectedRentingCommentForm
      {...props}
      ref={ref}
      initialData={initialData}
      currentData={currentData}
      onDataChange={handleDataChange}
    />
  );
});

RentingCommentFormWrapper.displayName = 'RentingCommentFormWrapper';

export default RentingCommentFormWrapper;
